import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Box, Divider, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
    SimpleButton,
    ErrorMessage,
    SimpleRadio,
    ControlledSingleSelect,
    ControlledTextField,
} from '@/src/ui';
import type { SchoolBaseDTO, SchoolCreateDTO } from '../models/schoolDTOs';
import { Constants } from '@/src/utils';

interface CreateEditSchoolModalProps {
    open: boolean;
    mode: 'create' | 'edit';
    school?: SchoolBaseDTO | null;
    onSave: (school: SchoolBaseDTO | SchoolCreateDTO) => void;
    onClose: () => void;
    error?: string;
}

const statusOptions = [
    { id: 1, name: 'Active' },
    { id: 2, name: 'Inactive' },
    { id: 3, name: 'Deleted' },
];

const CreateEditSchoolModal: React.FC<CreateEditSchoolModalProps> = ({
    open,
    mode,
    school,
    onSave,
    onClose,
    error,
}) => {
    const schema = yup.object().shape({
        name: yup
            .string()
            .required('School Name is required')
            .max(
                Constants.MAX_CHARS.SCHOOL_FIELDS,
                `School Name cannot exceed ${Constants.MAX_CHARS.SCHOOL_FIELDS} characters`
            ),
        code: yup
            .string()
            .required('Code is required')
            .max(
                Constants.MAX_CHARS.SCHOOL_FIELDS,
                `Code cannot exceed ${Constants.MAX_CHARS.SCHOOL_FIELDS} characters`
            ),
        externalIdentifier: yup
            .string()
            .required('External Identifier is required')
            .max(
                Constants.MAX_CHARS.SCHOOL_FIELDS,
                `External Identifier cannot exceed ${Constants.MAX_CHARS.SCHOOL_FIELDS} characters`
            ),
        districtId: yup
            .number()
            .typeError('District ID must be a number')
            .integer()
            .min(0)
            .required('District is required'),
        status: yup
            .number()
            .typeError('Status must be a number')
            .integer()
            .min(0)
            .required('Status is required'),
        enablePracticeOrInherit: yup.number().oneOf([0, 1, 2]).required(),
        enablePracticeChatOrInherit: yup.number().oneOf([0, 1, 2]).required(),
    });

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            name: school?.name || '',
            code: school?.code || '',
            externalIdentifier: school?.externalIdentifier || '',
            districtId: school?.districtId ?? 0,
            status: mode === 'create' ? 1 : (school?.status ?? 0),
            enablePracticeOrInherit:
                typeof school?.enablePracticeOrInherit === 'number'
                    ? school.enablePracticeOrInherit
                    : 2,
            enablePracticeChatOrInherit:
                typeof school?.enablePracticeChatOrInherit === 'number'
                    ? school.enablePracticeChatOrInherit
                    : 2,
        },
    });

    const [practiceOrInherit, setPracticeOrInherit] = useState<number>(
        typeof school?.enablePracticeOrInherit === 'number' ? school.enablePracticeOrInherit : 2
    );
    const [practiceChatOrInherit, setPracticeChatOrInherit] = useState<number>(
        typeof school?.enablePracticeChatOrInherit === 'number'
            ? school.enablePracticeChatOrInherit
            : 2
    );

    useEffect(() => {
        if (open) {
            setPracticeOrInherit(
                typeof school?.enablePracticeOrInherit === 'number'
                    ? school.enablePracticeOrInherit
                    : 2
            );
            setPracticeChatOrInherit(
                typeof school?.enablePracticeChatOrInherit === 'number'
                    ? school.enablePracticeChatOrInherit
                    : 2
            );
            reset({
                name: school?.name || '',
                code: school?.code || '',
                externalIdentifier: school?.externalIdentifier || '',
                districtId: school?.districtId ?? 0,
                status: mode === 'create' ? 1 : (school?.status ?? 0),
                enablePracticeOrInherit:
                    typeof school?.enablePracticeOrInherit === 'number'
                        ? school.enablePracticeOrInherit
                        : 2,
                enablePracticeChatOrInherit:
                    typeof school?.enablePracticeChatOrInherit === 'number'
                        ? school.enablePracticeChatOrInherit
                        : 2,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, school, reset]);

    useEffect(() => {
        setValue('enablePracticeOrInherit', practiceOrInherit);
    }, [practiceOrInherit, setValue]);
    useEffect(() => {
        setValue('enablePracticeChatOrInherit', practiceChatOrInherit);
    }, [practiceChatOrInherit, setValue]);

    const onSubmit = useCallback(
        (data: any) => {
            const payload = {
                ...data,
                enablePracticeOrInherit: practiceOrInherit,
                enablePracticeChatOrInherit: practiceChatOrInherit,
            };
            if (mode === 'edit') {
                const editPayload = {
                    ...payload,
                    id: school?.id,
                    districtName: school?.districtName || '',
                };
                onSave(editPayload);
            } else {
                onSave(payload);
            }
        },
        [mode, onSave, school, practiceOrInherit, practiceChatOrInherit]
    );

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    p: 4,
                    background: 'white',
                    borderRadius: 2,
                    maxWidth: 700,
                    width: '90%',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                }}
            >
                <Typography variant="h6" sx={{ mb: 1 }}>
                    {mode === 'edit' ? 'Edit School' : 'Add School'}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <ControlledTextField
                            label="School Name"
                            name="name"
                            error={errors.name}
                            control={control}
                            maxLength={Constants.MAX_CHARS.SCHOOL_FIELDS}
                        />
                        <ControlledTextField
                            label="Code"
                            name="code"
                            error={errors.code}
                            control={control}
                            maxLength={Constants.MAX_CHARS.SCHOOL_FIELDS}
                        />
                        <ControlledTextField
                            label="External Identifier"
                            name="externalIdentifier"
                            error={errors.externalIdentifier}
                            control={control}
                            maxLength={Constants.MAX_CHARS.SCHOOL_FIELDS}
                        />
                        <ControlledTextField
                            label="District ID"
                            name="districtId"
                            error={errors.districtId}
                            control={control}
                            type="number"
                        />
                        {mode === 'edit' && (
                            <ControlledSingleSelect
                                name="status"
                                label="Status"
                                options={statusOptions}
                                control={control}
                                onChange={(e) => setValue('status', Number(e.target.value))}
                                sx={{ width: '100%' }}
                                error={errors.status}
                            />
                        )}
                        <Box>
                            <Typography variant="subtitle2">Practice</Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <SimpleRadio
                                    name="enablePracticeOrInherit"
                                    label="Enable"
                                    value="1"
                                    checked={practiceOrInherit === 1}
                                    onChange={() => setPracticeOrInherit(1)}
                                />
                                <SimpleRadio
                                    name="enablePracticeOrInherit"
                                    label="Disable"
                                    value="0"
                                    checked={practiceOrInherit === 0}
                                    onChange={() => setPracticeOrInherit(0)}
                                />
                                <SimpleRadio
                                    name="enablePracticeOrInherit"
                                    label="Inherit"
                                    value="2"
                                    checked={practiceOrInherit === 2}
                                    onChange={() => setPracticeOrInherit(2)}
                                />
                            </Box>
                            {errors.enablePracticeOrInherit && (
                                <Typography color="error" variant="caption">
                                    {errors.enablePracticeOrInherit.message}
                                </Typography>
                            )}
                        </Box>

                        <Box>
                            <Typography variant="subtitle2">Practice Chat</Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <SimpleRadio
                                    name="enablePracticeChatOrInherit"
                                    label="Enable"
                                    value="1"
                                    checked={practiceChatOrInherit === 1}
                                    onChange={() => setPracticeChatOrInherit(1)}
                                />
                                <SimpleRadio
                                    name="enablePracticeChatOrInherit"
                                    label="Disable"
                                    value="0"
                                    checked={practiceChatOrInherit === 0}
                                    onChange={() => setPracticeChatOrInherit(0)}
                                />
                                <SimpleRadio
                                    name="enablePracticeChatOrInherit"
                                    label="Inherit"
                                    value="2"
                                    checked={practiceChatOrInherit === 2}
                                    onChange={() => setPracticeChatOrInherit(2)}
                                />
                            </Box>
                            {errors.enablePracticeChatOrInherit && (
                                <Typography color="error" variant="caption">
                                    {errors.enablePracticeChatOrInherit.message}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <ErrorMessage error={error} />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                        <SimpleButton type="submit" variant="contained" color="primary">
                            {mode === 'edit' ? 'Save Changes' : 'Add School'}
                        </SimpleButton>
                        <SimpleButton variant="outlined" onClick={onClose}>
                            Cancel
                        </SimpleButton>
                    </Box>
                </form>
            </Box>
        </Modal>
    );
};

export default CreateEditSchoolModal;
